<script setup>
import { getInboxLabel } from 'dashboard/helper/inboxLabel';
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappTemplatesPage from '../settings/inbox/settingsPage/WhatsappTemplatesPage.vue';
import { cloudInboxes, distributeTemplate } from './templateDistribution';

const store = useStore();
const route = useRoute();
const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');
const availableInboxes = computed(() => cloudInboxes(inboxes.value));
const selectedId = ref('');
const scope = ref('selected');
const loading = ref(true);
const loadFailed = ref(false);
const busy = ref(false);
const editorBusy = ref(false);
const results = ref([]);
const pendingRequest = ref(null);
const locked = computed(() => busy.value || editorBusy.value);
const selectedInbox = computed(() =>
  availableInboxes.value.find(inbox => String(inbox.id) === selectedId.value)
);
const inboxOptions = computed(() =>
  availableInboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: getInboxLabel(inbox),
    icon: 'i-simple-icons-whatsapp',
  }))
);
const scopeOptions = computed(() => [
  { value: 'selected', label: t('META_TEMPLATES.SELECTED') },
  { value: 'all', label: t('META_TEMPLATES.ALL') },
]);
const creationContext = computed(() =>
  scope.value === 'all'
    ? t('META_TEMPLATES.CONTEXT_ALL', { count: availableInboxes.value.length })
    : t('META_TEMPLATES.CONTEXT_SELECTED', { name: selectedInbox.value?.name })
);
const failures = computed(() =>
  results.value.filter(result => !result.success)
);

watch(
  availableInboxes,
  value => {
    if (!value.some(inbox => String(inbox.id) === selectedId.value))
      selectedId.value = value.length ? String(value[0].id) : '';
  },
  { immediate: true }
);

async function loadInboxes() {
  loading.value = true;
  loadFailed.value = false;
  try {
    await store.dispatch('inboxes/getFromNetwork');
  } catch {
    loadFailed.value = true;
    useAlert(t('META_TEMPLATES.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

watch(
  () => route.params.accountId,
  () => {
    results.value = [];
    pendingRequest.value = null;
    selectedId.value = '';
    scope.value = 'selected';
    loadInboxes();
  }
);

async function sendRequest() {
  if (busy.value || !pendingRequest.value) return;
  busy.value = true;
  try {
    results.value = await distributeTemplate({
      ...pendingRequest.value,
      previousResults: results.value,
      errorMessage: t('META_TEMPLATES.SEND_ERROR'),
      onProgress: value => {
        results.value = value;
      },
    });
    useAlert(
      t(
        failures.value.length
          ? 'META_TEMPLATES.PARTIAL'
          : 'META_TEMPLATES.SUCCESS'
      )
    );
    // Refresh cached records only. Creating already enqueues the Meta sync.
    await store.dispatch('inboxes/getFromNetwork');
  } catch {
    useAlert(t('META_TEMPLATES.LOAD_ERROR'));
  } finally {
    busy.value = false;
    if (!failures.value.length) pendingRequest.value = null;
  }
}

async function createTemplate(template, file) {
  results.value = [];
  pendingRequest.value = {
    inboxes:
      scope.value === 'all'
        ? [...availableInboxes.value]
        : [selectedInbox.value],
    preparedInbox: selectedInbox.value,
    template,
    file,
  };
  await sendRequest();
}

function guardNavigation() {
  if (!locked.value) return true;
  useAlert(t('META_TEMPLATES.WAIT'));
  return false;
}
function guardUnload(event) {
  if (!locked.value) return;
  event.preventDefault();
  event.returnValue = '';
}
onBeforeRouteLeave(guardNavigation);
onBeforeRouteUpdate(guardNavigation);
onMounted(() => {
  loadInboxes();
  window.addEventListener('beforeunload', guardUnload);
});
onBeforeUnmount(() => window.removeEventListener('beforeunload', guardUnload));
</script>

<template>
  <main class="h-full min-h-0 w-full overflow-y-auto bg-n-surface-1">
    <div
      class="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 pb-24 sm:px-6"
    >
      <header class="flex items-start gap-3">
        <span
          class="grid size-12 shrink-0 place-items-center rounded-xl bg-n-teal-3 text-n-teal-11"
          aria-hidden="true"
        >
          <span class="i-hugeicons-message-edit-01 size-6" />
        </span>
        <div>
          <p class="mb-1 text-xs font-semibold text-n-brand">
            {{ t('META_TEMPLATES.SECTION') }}
          </p>
          <h1 class="mb-2 text-2xl font-semibold text-n-slate-12">
            {{ t('META_TEMPLATES.MENU') }}
          </h1>
          <p class="m-0 text-sm text-n-slate-11">
            {{ t('META_TEMPLATES.DESCRIPTION') }}
          </p>
        </div>
      </header>
      <p v-if="loading" role="status" class="text-sm text-n-slate-11">
        {{ t('META_TEMPLATES.LOADING') }}
      </p>
      <div
        v-else-if="loadFailed"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-6"
      >
        <p>{{ t('META_TEMPLATES.LOAD_ERROR') }}</p>
        <NextButton :label="t('META_TEMPLATES.RELOAD')" @click="loadInboxes" />
      </div>
      <div
        v-else-if="!availableInboxes.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-6 text-sm text-n-slate-11"
      >
        {{ t('META_TEMPLATES.EMPTY') }}
      </div>
      <template v-else>
        <section
          class="grid gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5 md:grid-cols-2"
        >
          <label class="m-0 flex flex-col gap-2 text-sm text-n-slate-12">
            {{ t('META_TEMPLATES.INBOX') }}
            <ComboBox
              v-model="selectedId"
              :options="inboxOptions"
              :placeholder="t('META_TEMPLATES.SELECT_INBOX')"
              :search-placeholder="t('META_TEMPLATES.SEARCH_INBOX')"
              :empty-state="t('META_TEMPLATES.NO_INBOX_RESULTS')"
              :disabled="locked"
              teleport-to-body
            />
          </label>
          <label class="m-0 flex flex-col gap-2 text-sm text-n-slate-12">
            {{ t('META_TEMPLATES.DESTINATION') }}
            <NextSelect
              v-model="scope"
              :options="scopeOptions"
              class="w-full [&>select]:w-full"
              :disabled="locked"
            />
          </label>
          <p class="m-0 text-xs leading-5 text-n-slate-11 md:col-span-2">
            {{ t('META_TEMPLATES.SHARED') }}
          </p>
        </section>
        <section
          v-if="results.length"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
          aria-live="polite"
        >
          <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('META_TEMPLATES.RESULTS') }}
            </h2>
            <NextButton
              v-if="failures.length"
              :label="t('META_TEMPLATES.RETRY')"
              :disabled="locked"
              :is-loading="busy"
              @click="sendRequest"
            />
          </div>
          <ul class="m-0 list-none space-y-2 p-0">
            <li
              v-for="result in results"
              :key="result.inboxId"
              class="flex flex-wrap items-center gap-2 text-sm"
            >
              <span
                :class="
                  result.success
                    ? 'i-hugeicons-checkmark-circle-02 text-n-teal-11'
                    : 'i-hugeicons-alert-circle text-n-ruby-11'
                "
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <strong class="text-n-slate-12">{{ result.name }}</strong>
              <span class="text-n-slate-11">{{
                result.success ? t('META_TEMPLATES.SENT') : result.error
              }}</span>
            </li>
          </ul>
        </section>
        <div
          :inert="busy"
          class="rounded-xl border border-n-weak bg-n-solid-1 py-5"
        >
          <WhatsappTemplatesPage
            v-if="selectedInbox"
            :key="`${route.params.accountId}:${selectedInbox.id}`"
            :inbox="selectedInbox"
            :create-template="createTemplate"
            :creation-context="creationContext"
            @busy="editorBusy = $event"
          />
        </div>
      </template>
    </div>
  </main>
</template>
